import React, { useState, useEffect, useCallback } from 'react';
import Modal from 'react-modal';
import Select from 'react-select';
import api from '../services/api';
import Swal from 'sweetalert2';
import { FiTrash2, FiPlus } from 'react-icons/fi';

const DesinscriptionsTemporairesModal = ({ isOpen, onClose, inscriptionId, inscriptionData }) => {
  const [desinscriptions, setDesinscriptions] = useState([]);
  const [periodes, setPeriodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [form, setForm] = useState({
    periode_id: '',
    date_inscription: new Date().toISOString().slice(0, 10),
    motif: '',
    date_reinscription: ''
  });

  const fetchDesinscriptions = useCallback(async () => {
    if (!inscriptionId) return;
    try {
      const res = await api.get(`/desinscriptions-temporaires/inscription/${inscriptionId}`);
      setDesinscriptions(res.data.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des désinscriptions:', error);
      Swal.fire('Erreur', 'Impossible de charger les désinscriptions temporaires', 'error');
    }
  }, [inscriptionId]);

  const fetchPeriodes = useCallback(async () => {
    try {
      const res = await api.get('/periodes');
      setPeriodes(res.data.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des périodes:', error);
    }
  }, []);

  useEffect(() => {
    if (isOpen && inscriptionId) {
      fetchDesinscriptions();
      fetchPeriodes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, inscriptionId, fetchDesinscriptions, fetchPeriodes]);

  const handleAdd = () => {
    setForm({
      periode_id: '',
      date_inscription: new Date().toISOString().slice(0, 10),
      motif: '',
      date_reinscription: ''
    });
    setShowAddForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.periode_id) {
      Swal.fire('Erreur', 'Veuillez sélectionner une période', 'error');
      return;
    }
    if (!form.date_inscription) {
      Swal.fire('Erreur', 'Veuillez saisir la date de désinscription', 'error');
      return;
    }

    setLoading(true);
    try {
      await api.post('/desinscriptions-temporaires', {
        inscription_id: inscriptionId,
        periode_id: form.periode_id,
        date_inscription: form.date_inscription,
        motif: form.motif || null,
        date_reinscription: form.date_reinscription || null
      });
      await fetchDesinscriptions();
      setShowAddForm(false);
      setForm({
        periode_id: '',
        date_inscription: new Date().toISOString().slice(0, 10),
        motif: '',
        date_reinscription: ''
      });
      Swal.fire('Succès', 'Désinscription temporaire ajoutée avec succès', 'success');
    } catch (error) {
      const message = error.response?.data?.message || 'Erreur lors de l\'ajout de la désinscription temporaire';
      Swal.fire('Erreur', message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Supprimer cette désinscription temporaire ?',
      text: 'Cette action est irréversible.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler',
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/desinscriptions-temporaires/${id}`);
        await fetchDesinscriptions();
        Swal.fire('Supprimé !', 'La désinscription temporaire a été supprimée.', 'success');
      } catch (error) {
        Swal.fire('Erreur', 'Impossible de supprimer la désinscription temporaire', 'error');
      }
    }
  };

  const periodesOptions = periodes.map(p => ({
    value: p.id,
    label: p.periode || `Période ${p.id}`
  }));

  const formatDateFR = (dateStr) => {
    if (!dateStr) return '';
    const [y, m, d] = dateStr.split('-');
    return `${d}/${m}/${y}`;
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel="Gestion des désinscriptions temporaires"
      ariaHideApp={false}
      style={{
        overlay: {
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          zIndex: 1000
        },
        content: {
          maxWidth: '800px',
          width: '100%',
          margin: 'auto',
          borderRadius: 12,
          padding: 0,
          maxHeight: '90vh',
          overflow: 'auto'
        }
      }}
    >
      <div className="p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">
            Gestion des désinscriptions temporaires
            {inscriptionData && (
              <span className="text-sm font-normal text-gray-600 ml-2">
                - {inscriptionData.nom} {inscriptionData.prenom}
              </span>
            )}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 text-2xl font-bold focus:outline-none"
            title="Fermer"
          >
            ×
          </button>
        </div>

        {/* Formulaire d'ajout */}
        {showAddForm && (
          <div className="mb-4 p-4 bg-gray-50 rounded-lg border">
            <h3 className="text-lg font-medium mb-3">Ajouter une désinscription temporaire</h3>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Période *</label>
                  <Select
                    options={periodesOptions}
                    value={periodesOptions.find(o => o.value === form.periode_id) || null}
                    onChange={opt => setForm({ ...form, periode_id: opt ? opt.value : '' })}
                    isClearable
                    placeholder="Sélectionner une période"
                    classNamePrefix="react-select"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Date de désinscription *</label>
                  <input
                    type="date"
                    value={form.date_inscription}
                    onChange={e => setForm({ ...form, date_inscription: e.target.value })}
                    className="block w-full rounded-lg border border-gray-300 px-3 py-2"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Motif</label>
                  <textarea
                    value={form.motif}
                    onChange={e => setForm({ ...form, motif: e.target.value })}
                    className="block w-full rounded-lg border border-gray-300 px-3 py-2"
                    rows="3"
                    placeholder="Raison de la désinscription temporaire"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Date de réinscription</label>
                  <input
                    type="date"
                    value={form.date_reinscription}
                    onChange={e => setForm({ ...form, date_reinscription: e.target.value })}
                    className="block w-full rounded-lg border border-gray-300 px-3 py-2"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Optionnel - Date prévue de réinscription
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  {loading ? 'Ajout...' : 'Ajouter'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    setForm({
                      periode_id: '',
                      date_inscription: new Date().toISOString().slice(0, 10),
                      motif: '',
                      date_reinscription: ''
                    });
                  }}
                  className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Bouton pour afficher le formulaire */}
        {!showAddForm && (
          <button
            onClick={handleAdd}
            className="mb-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2"
          >
            <FiPlus /> Ajouter une désinscription temporaire
          </button>
        )}

        {/* Liste des désinscriptions temporaires */}
        <div className="mt-4">
          {desinscriptions.length === 0 ? (
            <p className="text-gray-500 text-center py-8">Aucune désinscription temporaire enregistrée</p>
          ) : (
            <div className="space-y-2">
              <table className="min-w-full divide-y divide-gray-200 border rounded-lg">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Période</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date désinscription</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Motif</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date réinscription</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {desinscriptions.map(d => (
                    <tr key={d.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap">
                        {d.periode || `Période ${d.periode_id}`}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {formatDateFR(d.date_inscription)}
                      </td>
                      <td className="px-4 py-3">
                        {d.motif || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {d.date_reinscription ? formatDateFR(d.date_reinscription) : '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        <button
                          onClick={() => handleDelete(d.id)}
                          className="text-red-600 hover:text-red-900 p-2 rounded-full hover:bg-red-50"
                          title="Supprimer"
                        >
                          <FiTrash2 size={18} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default DesinscriptionsTemporairesModal;

